import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import TableView from "../components/TableView";
import ProductTableAction from "../generatedTableActions/ProductTableAction";
import Button from "../components/Button";
import Badge from "../components/Badge";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  setCurrentPage,
  setPerPage,
  setLoading,
  setProduct,
  setSearchTerm,
  setTotalPage,
  setTotalCount
} from "../generatedSlice/productSlice";
import { getProduct } from "../generatedRequest/product";
import { toast } from "react-toastify";
import TableSkeleton from "../components/TableSkeleton";
import ProductList from "../generatedTableList/ProductList";
import { TTableProps } from "../types/TableView";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TOptionType } from "../types/CustomSelect";
import ProductFilters from "../generatedFilters/ProductsFilters";
import { getRegion } from "../app/request/regionRequest";
import UploadProductFormModal from "../generatedFormModal/UploadProductFormModal";
import { ActionCells } from "../components/ActionCells";
import { toAbsoluteUrl } from "../_metronic/helpers";
import MasterFormModal from "../generatedFormModal/MasterFormModal";
import { debounce } from 'lodash';

const actions = ["Manually", "Upload-Excel"];

const FILTER_STORAGE_KEY = 'product_filters';
const NAVIGATION_STORAGE_KEY = 'product_navigation';

const ProductPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialLoadRef = useRef(true);
  const isResettingRef = useRef(false);
  const isInitialMount = useRef(true); // New ref to track first render

  const isFromEditPage = searchParams.toString().length > 0;

  // Check if coming from back button
  const checkBackButtonNavigation = () => {
    const navData = localStorage.getItem(NAVIGATION_STORAGE_KEY);
    if (navData) {
      try {
        const parsedData = JSON.parse(navData);
        const isRecentNavigation = (Date.now() - parsedData.timestamp) < 5 * 60 * 1000;
        return parsedData.fromBackButton && isRecentNavigation;
      } catch (error) {
        console.error('Error parsing navigation data:', error);
        return false;
      }
    }
    return false;
  };

  const isFromBackButton = checkBackButtonNavigation();

  const [filters, setFilters] = useState({
    status: { label: "", value: "" },
    region: "",
    category: "",
    subcategory: "",
    brand: "",
    initialized: false,
    fromUrl: false,
    fromLocalStorage: false,
  });

  const [filtersReady, setFiltersReady] = useState(false);
  const [allNewRegion, setAllNewRegion] = useState([]);
  const [initload, setInitload] = useState(true);
  const [filterloading, setFilterLoading] = useState(false);
  const [uploadExcel, setUploadExcel] = useState(false);
  const [addMasterModel, setAddMasterModel] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const {
    data,
    perPage,
    currentPage,
    totalPage,
    searchTerm,
    loading,
    sortBy,
    sortColumn,
    rerender1,
    totalCount
  } = useSelector((state: RootState) => state.product);

const [userPermissions, setUserPermissions] = useState({ canAdd: false });
  const [showPermissionPrompt, setShowPermissionPrompt] = useState<boolean>(false);
  // Get user permissions from localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const userDataString = localStorage.getItem('kt-auth-react-v');

        if (!userDataString) {
          setUserPermissions({ canAdd: false });
          return;
        }

        const userData = JSON.parse(userDataString);
        const accessoryPermissions = userData?.data?.permissions?.find(
          (permission: any) => permission.moduleName === "Product"
        );

        if (accessoryPermissions) {
          const addActivity = accessoryPermissions.moduleActivities.find(
            (activity: any) => activity.activityFunction === "add"
          );
          const canAdd = addActivity?.isSelected || false;
          setUserPermissions({ canAdd });
        } else {
          setUserPermissions({ canAdd: false });
        }
      } catch (error) {
        console.error("Error parsing user permissions:", error);
        setUserPermissions({ canAdd: false });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, []);


  // Save filters to localStorage
  const saveFiltersToLocalStorage = useCallback((filtersData: any) => {
    try {
      const filterDataToSave = {
        status: filtersData.status,
        region: filtersData.region,
        category: filtersData.category,
        subcategory: filtersData.subcategory,
        brand: filtersData.brand,
        searchTerm: searchTerm,
        currentPage: currentPage,
        perPage: perPage,
        timestamp: Date.now(),
      };
      // console.log('Saving filters to localStorage:', filterDataToSave);
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterDataToSave));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [searchTerm, currentPage, perPage]);

  // Load filters from localStorage
  const loadFiltersFromLocalStorage = useCallback(() => {
    try {
      const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        // console.log(parsedFilters, "data");
        const isDataFresh = (Date.now() - parsedFilters.timestamp) < 60 * 60 * 1000;
        if (isDataFresh) {
          return parsedFilters;
        } else {
          localStorage.removeItem(FILTER_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    return null;
  }, []);

  // Clear navigation data
  const clearNavigationData = useCallback(() => {
    localStorage.removeItem(NAVIGATION_STORAGE_KEY);
  }, []);

  // Synchronous filter initialization
  if (!filters.initialized) {
    // console.log('Synchronous filter initialization started, isFromBackButton:', isFromBackButton, 'isFromEditPage:', isFromEditPage);
    if (isFromBackButton) {
      const savedFilters = loadFiltersFromLocalStorage();
      if (savedFilters) {
        if (savedFilters.searchTerm) {
          dispatch(setSearchTerm(savedFilters.searchTerm));
        }
        if (savedFilters.currentPage) {
          dispatch(setCurrentPage(savedFilters.currentPage));
        }
        if (savedFilters.perPage) {
          dispatch(setPerPage(savedFilters.perPage));
        }

        setFilters({
          status: savedFilters.status || { label: "", value: "" },
          region: savedFilters.region || "",
          category: savedFilters.category || "",
          subcategory: savedFilters.subcategory || "",
          brand: savedFilters.brand || "",
          initialized: true,
          fromUrl: false,
          fromLocalStorage: true,
        });

        setFiltersReady(true);
        clearNavigationData();
      } else {
        setFilters(prev => ({ ...prev, initialized: true, fromLocalStorage: false }));
        setFiltersReady(true);
      }
    } else if (isFromEditPage) {
      const urlStatus = searchParams.get('status');
      const urlRegion = searchParams.get('region');
      const urlCategory = searchParams.get('category');
      const urlSubcategory = searchParams.get('subcategory');
      const urlBrand = searchParams.get('brand');
      const urlSearch = searchParams.get('search');
      const urlPage = searchParams.get('page');
      const urlLimit = searchParams.get('limit');

      // console.log('Initializing from URL, urlSearch:', urlSearch);

      if (urlSearch) {
        dispatch(setSearchTerm(urlSearch));
      }
      if (urlPage) {
        dispatch(setCurrentPage(parseInt(urlPage)));
      }
      if (urlLimit) {
        dispatch(setPerPage(parseInt(urlLimit)));
      }

      setFilters({
        status: urlStatus ? {
          label: urlStatus.charAt(0).toUpperCase() + urlStatus.slice(1),
          value: urlStatus,
        } : { label: "", value: "" },
        region: urlRegion || "",
        category: urlCategory || "",
        subcategory: urlSubcategory || "",
        brand: urlBrand || "",
        initialized: true,
        fromUrl: true,
        fromLocalStorage: false,
      });

      setFiltersReady(true);
    } else {
      const savedFilters = loadFiltersFromLocalStorage();
      if (savedFilters) {
        if (savedFilters.searchTerm) {
          dispatch(setSearchTerm(savedFilters.searchTerm));
        }
        if (savedFilters.currentPage) {
          dispatch(setCurrentPage(savedFilters.currentPage));
        }
        if (savedFilters.perPage) {
          dispatch(setPerPage(savedFilters.perPage));
        }

        setFilters({
          status: savedFilters.status || { label: "", value: "" },
          region: savedFilters.region || "",
          category: savedFilters.category || "",
          subcategory: savedFilters.subcategory || "",
          brand: savedFilters.brand || "",
          initialized: true,
          fromUrl: false,
          fromLocalStorage: true,
        });

        setFiltersReady(true);
      } else {
        setFilters(prev => ({ ...prev, initialized: true, fromUrl: false, fromLocalStorage: false }));
        setFiltersReady(true);
      }
    }
    // console.log('Synchronous filter initialization completed, filters:', filters, 'filtersReady:', filtersReady);
  }

  // Save filters when they change (except during initialization)
  useEffect(() => {
    if (filters.initialized && !filters.fromUrl && !filters.fromLocalStorage && !initialLoadRef.current && !isResettingRef.current) {
      saveFiltersToLocalStorage(filters);
    }
  }, [filters, saveFiltersToLocalStorage]);

  // Handle page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (filters.initialized && !initialLoadRef.current) {
        saveFiltersToLocalStorage(filters);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (filters.initialized && !initialLoadRef.current) {
        saveFiltersToLocalStorage(filters);
      }
    };
  }, [filters, saveFiltersToLocalStorage]);

  // Build query parameters
  const buildQueryParams = useCallback((customParams = {}) => {
    const params = {
      page: currentPage,
      limit: perPage,
      search: searchTerm,
      status: filters.status?.value || '',
      region: filters.region,
      category: filters.category,
      subcategory: filters.subcategory,
      brand: filters.brand,
      ...customParams,
    };

    const filteredParams = Object.entries(params)
      .filter(([key, value]) => {
        if (value === '' || value === undefined || value === null) return false;
        if (key === 'status' && typeof value === 'object' && (!value || (value as any).value === '')) return false;
        return true;
      })
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    return filteredParams;
  }, [currentPage, perPage, searchTerm, filters]);

  // Current query string
  const currentQueryString = useMemo(() => {
    const query = buildQueryParams();
    // console.log('currentQueryString changed:', query);
    return query;
  }, [buildQueryParams]);

  // Load regions data
  useEffect(() => {
    const fn = async () => {
      try {
        const response = await getRegion(`page=1&limit=100`);
        if (response.data.status) {
          setAllNewRegion(response.data.data.data);
        } else {
          toast.error("Something went wrong");
        }
      } catch (error) {
        toast.error("Network Error");
      }
    };
    fn();
  }, []);

  // Centralized data fetching
  const fetchData = useCallback(
    debounce(async () => {
      // console.log('fetchData called with query:', currentQueryString);
      setFilterLoading(true);
      dispatch(setLoading(true));

      try {
        const response = await getProduct(currentQueryString);
        // console.log(response, "res");
        if (response.data.status) {
          dispatch(setProduct(response.data.data.data));
          dispatch(setTotalCount(Math.ceil(response.data.data.totalCount)))
          dispatch(setTotalPage(Math.ceil(response.data.data.totalCount / perPage)));
        } else {
          toast.error("Something went wrong");
        }
      } catch (error) {
        toast.error("Network Error");
      } finally {
        dispatch(setLoading(false));
        setInitload(false);
        setFilterLoading(false);
        initialLoadRef.current = false;
        // console.log('fetchData completed');
      }
    }, 300),
    [currentQueryString, dispatch, perPage]
  );

  useEffect(() => {
    if (!filters.initialized || isResettingRef.current) return;
    // console.log('Triggering fetchData with filters.initialized:', filters.initialized);
    fetchData();
  }, [fetchData, filters.initialized , rerender1]);

  // Clear URL params after data load from edit page
  useEffect(() => {
    if (filters.fromUrl && !loading && data.length > 0 && !initialLoadRef.current) {
      // console.log('Preparing to clear URL parameters, current searchTerm:', searchTerm);
      saveFiltersToLocalStorage(filters);
      const timer = setTimeout(() => {
        // console.log('Clearing URL parameters');
        setSearchParams({});
        setFilters(prev => ({ ...prev, fromUrl: false }));
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [filters.fromUrl, loading, data, setSearchParams, searchTerm, saveFiltersToLocalStorage]);

  // Clean up search term on unmount
  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('/product')) {
        // console.log('Unmounting ProductPage, clearing searchTerm');
        dispatch(setSearchTerm(''));
      }
    };
  }, [dispatch]);

  // Debug render sequence
  useEffect(() => {
    // console.log('Render occurred, isInitialMount:', isInitialMount.current, 'filters.initialized:', filters.initialized, 'filtersReady:', filtersReady, 'loading:', loading, 'initload:', initload);
    isInitialMount.current = false; // Update after first render
  }, [filters.initialized, filtersReady, loading, initload]);

  // Handle edit navigation
  const handleEditNavigation = useCallback((editUrl: string) => {
    saveFiltersToLocalStorage(filters);
    const navData = {
      fromBackButton: true,
      timestamp: Date.now(),
    };
    localStorage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(navData));
    navigate(editUrl);
  }, [filters, saveFiltersToLocalStorage, navigate]);

  // Selection logic
  const allIds = Array.isArray(data) ? data.map((item: any) => item._id) : [];
  const isAllSelected = selectedRows.length === allIds.length && allIds.length > 0;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allIds);
    }
  };

  const toggleRow = (id: string) => {
    setSelectedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  // Table columns
  const columns = [
    {
      name: "id",
      renderHeader: () => (
        <div className='form-check form-check-custom'>
          <input
            className='form-check-input'
            type='checkbox'
            data-kt-check={''}
            onChange={toggleSelectAll}
            checked={isAllSelected}
            style={{ margin: "0 3px" }}
          />
        </div>
      ),
      renderCell: (row: any) => (
        <div className='form-check form-check-custom'>
          <input
            className='form-check-input'
            type='checkbox'
            data-kt-check={''}
            style={{ margin: "0 3px" }}
            checked={selectedRows.includes(row._id)}
            onChange={() => toggleRow(row._id)}
          />
        </div>
      ),
    },
    {
      name: "image",
      renderHeader: (rows: any) => (
        <>
          <b>Image</b>
        </>
      ),
      renderCell: (row: any) => (
        <a
          href={`${row && row.productImages && row.productImages[0]}`}
          target="_blank"
          style={{ height: "50px", width: "50px", display: "block", margin: "0 auto" }}
        >
          <img
            src={`${row && row.productImages && row.productImages[0]}`}
            style={{ height: "100%", width: "100%", objectFit: "cover" }}
          />
        </a>
      ),
    },
    {
      name: "name",
      renderHeader: (rows: any) => (
        <>
          <b>Name</b>
        </>
      ),
      renderCell: (row: any) => (
        <>
          {row && (
            <>
              {row.displayName}
            </>
          )}
        </>
      ),
    },
    {
      name: "modelNumber",
      renderHeader: (rows: any) => (
        <>
          <b>Model Number</b>
        </>
      ),
      renderCell: (row: any) => <>{(row && row.manufacturerPartNumber) ? row.manufacturerPartNumber : row.modelNumber}</>,
    },
    {
      name: "brand",
      renderHeader: (rows: any) => (
        <>
          <b>Brand</b>
        </>
      ),
      renderCell: (row: any) => <>{row && row.brand}</>,
    },
    {
      name: "status",
      renderHeader: (rows: any) => (
        <>
          <b>Status</b>
        </>
      ),
      renderCell: (row: any) => (
        <Badge
          variant={row.status == "active" ? "success" : "danger"}
          className="fs-7 fw-bolder"
        >
          {row.status}
        </Badge>
      ),
    },
    {
      name: "subcategory",
      renderHeader: (rows: any) => (
        <>
          <b>Subcategory</b>
        </>
      ),
      renderCell: (row: any) => (
        <>
          {row?.subcategory && (
            <span>
              <a
                href={`/subcategory/edit?q=${row.subcategory._id}`}
                style={{ textDecoration: "underline" }}
                onClick={(e) => {
                  e.preventDefault();
                  handleEditNavigation(`/subcategory/edit?q=${row.subcategory._id}`);
                }}
              >
                {row.subcategory.subcategoryName}
              </a>
            </span>
          )}
          {row?.accessorySubcategory && (
            <span>
              <a
                href={`/subcategory/edit?q=${row.accessorySubcategory._id}`}
                style={{ textDecoration: "underline" }}
                onClick={(e) => {
                  e.preventDefault();
                  handleEditNavigation(`/subcategory/edit?q=${row.accessorySubcategory._id}`);
                }}
              >
                {row.accessorySubcategory.subcategoryName}
              </a>
            </span>
          )}
        </>
      ),
    },
    {
      name: "regions",
      renderHeader: (rows: any) => (
        <>
          <b>Region</b>
        </>
      ),
      renderCell: (row: any) => (
        <>
          {row?.regions && Array.prototype.map.call(row.regions, function (item) {
            return (
              <span key={item._id}>
                <a
                  href={"/region/edit?q=" + item._id}
                  style={{ textDecoration: "underline" }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleEditNavigation(`/region/edit?q=${item._id}`);
                  }}
                >
                  {item.regionName}
                </a>
                {"  "}
              </span>
            );
          })}
        </>
      ),
    },
    {
      name: "created_at",
      renderHeader: (rows: any) => (
        <>
          <b>Created At</b>
        </>
      ),
      renderCell: (row: any) => <>{row.createdAt}</>,
    },
    {
      name: "Actions",
      renderHeader: (rows: any) => (
        <>
          <b>Actions</b>
        </>
      ),
      renderCell: (row: any) => (
        <ProductTableAction
          regions={allNewRegion}
          product={row}
          currentFilters={currentQueryString}
          onEditNavigation={handleEditNavigation}
        />
      ),
    },
  ];

  // Event handlers
  const handlePageChange = useCallback((value: number) => {
    // console.log('handlePageChange called with value:', value);
    dispatch(setCurrentPage(value));
  }, [dispatch]);

  const handlePerPageChange = useCallback((value: number) => {
    // console.log('handlePerPageChange called with value:', value);
    dispatch(setPerPage(value));
  }, [dispatch]);

  const handleSearchParams = useCallback(
    debounce((value: string) => {
      // console.log('handleSearchParams called with value:', value);
      if (value !== undefined && value !== '') {
        dispatch(setCurrentPage(1));
        dispatch(setSearchTerm(value));
      }
    }, 300),
    [dispatch]
  );
    const hasActiveFilters = useMemo(() => {
  const hasSearchTerm = searchTerm && searchTerm.trim() !== '';
            const hasStatusFilter = filters.status && filters.status.value !== '';
            const hasRegionFilter = filters.region && filters.region !== '';
            const hasCategoryFilter = filters.category && filters.category !== '';
            const hasSubcategoryFilter = filters.subcategory && filters.subcategory !== '';
            const hasBrandFilter = filters.brand && filters.brand !== '';

            return hasSearchTerm || hasStatusFilter || hasRegionFilter || hasCategoryFilter || hasSubcategoryFilter || hasBrandFilter;
}, [searchTerm, filters]);


  const handleStatusFilter = useCallback((data: TOptionType<string>) => {
    // console.log('handleStatusFilter called with data:', data);
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, status: data };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleBrandFilter = useCallback((data: any) => {
    // console.log('handleBrandFilter called with data:', data);
    const payload = data.map((value: any) => value.brandName).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, brand: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleRegionFilter = useCallback((data: any) => {
    // console.log('handleRegionFilter called with data:', data);
    const payload = data.map((value: any) => value.id).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, region: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleCategoryFilter = useCallback((data: any) => {
    // console.log('handleCategoryFilter called with data:', data);
    const payload = data.map((value: any) => value._id).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, category: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleSubCategoryFilter = useCallback((data: any) => {
    // console.log('handleSubCategoryFilter called with data:', data);
    const payload = data.map((value: any) => value._id).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, subcategory: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleUpload = useCallback(() => {
    setUploadExcel(!uploadExcel);
  }, [uploadExcel]);

  const handleMasterAdd = useCallback(() => {
    setAddMasterModel(!addMasterModel);
  }, [addMasterModel]);

  const handleClearAllFilters = useCallback(() => {
    // console.log('handleClearAllFilters called');
    isResettingRef.current = true;
    setFilterLoading(true);

    localStorage.removeItem(FILTER_STORAGE_KEY);
    dispatch(setCurrentPage(1));
    dispatch(setPerPage(10));
    dispatch(setSearchTerm(""));

    setFilters({
      status: { label: "", value: "" },
      region: "",
      category: "",
      subcategory: "",
      brand: "",
      initialized: true,
      fromUrl: false,
      fromLocalStorage: false,
    });

    isResettingRef.current = false;
  }, [dispatch]);

  // Table configuration
  const configuration: TTableProps = {
    columns: columns,
    data: data,
    selectedRows: selectedRows,
    rows: data,
    showPagination: true,
    showSearch: true,
    showPerPage: true,
    searchValue: searchTerm || '',
    paginationData: {
      perPage: perPage,
      currentPage: currentPage,
      totalPage: totalPage,
      totalCount: totalCount
    },
    showFilter: true,
    filters: (
      filtersReady ? (
        <>
          <div className="d-flex align-items-center justify-content-center gap-2">
            <ProductFilters
              key={`filters-${filters.initialized}-${filtersReady}`}
              onStatusFilterChange={handleStatusFilter}
              onRegionFilterChange={handleRegionFilter}
              currentRegionFilter={filters.region}
              currentStatusFilter={filters.status}
              onCategoryFilterChange={handleCategoryFilter}
              currentCategoryFilter={filters.category}
              onSubCategoryFilterChange={handleSubCategoryFilter}
              currentSubCategoryFilter={filters.subcategory}
              showOnlyProductSubCategory={true}
              onBrandFilterChange={handleBrandFilter}
              currentBrandFilter={filters.brand}
            />
            <div className="d-flex justify-content-end">
            <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleClearAllFilters}
                disabled={filterloading || !hasActiveFilters}
                style={{
                  fontSize: '12px',
                  padding: '12px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: (filterloading || !hasActiveFilters) ? "#ccc" : "#00A3FF",
                  color: "#fff",
                  border: 'none',
                  borderRadius: '4px',
                  zIndex: 1000,
                  cursor: (filterloading || !hasActiveFilters) ? 'not-allowed' : 'pointer',
                  opacity: (filterloading || !hasActiveFilters) ? 0.6 : 1,
                }}
              >
                {filterloading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '16px', color: "#fff" }}></i>
                    
                  </>
                ) : (
                  <>
                    <i className="fas fa-times" style={{ cursor: 'pointer', fontSize: '12px', color: "#fff" }}></i>
                    Reset
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div>Loading filters...</div>
      )
    ),
    onPageChange: handlePageChange,
    onPerPageChange: handlePerPageChange,
    handleSearchParams: handleSearchParams,
    searchPlaceholder: "Search Products",
    searchLoading: filterloading,
  };

 const handleSelect = (selectedAction: string) => {
  // console.log('handleSelect called with:', selectedAction);
  // console.log('Current permissions:', userPermissions); 
  
  switch (selectedAction) {
    case "Manually":
      if (userPermissions.canAdd) {
        navigate("add");
      } 
      break;
    case "Upload-Excel":
      if (userPermissions.canAdd) {
        handleUpload();
      } 
      break;
    default:
      break;
  }
};

  // Show skeleton during initial mount, filter initialization, or data fetching
  if (isInitialMount.current || !filters.initialized || !filtersReady || (loading && initload)) {
    // console.log('Rendering TableSkeleton, isInitialMount:', isInitialMount.current, 'filters.initialized:', filters.initialized, 'filtersReady:', filtersReady, 'loading:', loading, 'initload:', initload);
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <div className="d-flex align-items-center" style={{ gap: "5px" }}>
          <h2>Products </h2>
          <img
            alt="Logo"
            src={toAbsoluteUrl("/media/icons/duotune/art/exclamation-icon.svg")}
            className="h-20px cursor-pointer"
            onClick={() => navigate(`/helps?moduleName=Product`)}
          />
        </div>
        {userPermissions.canAdd ? (
          <ActionCells
            actions={actions}
            onSelect={handleSelect}
            title={"Add Product"}
            buttonStyle={`btn btn-light btn-primary btn-xl`}
            iconStyle={`svg-icon-4 ms-1`}
          />
        ) : (
          <button
            type="button"
            className="btn btn-light btn-secondary btn-xl"
            // onClick={() => setShowPermissionPrompt(true)}
            style={{ cursor: 'not-allowed', opacity: 0.6 }}
          >
            {/* <i className="bi bi-plus-lg"></i> */}
            Add Product
          </button>
        )}
      </div>
      <ProductList {...configuration} />

      {uploadExcel && (
        <UploadProductFormModal onClose={() => handleUpload()} />
      )}

      {addMasterModel && (
        <MasterFormModal onClose={() => handleMasterAdd()} />
      )}
    </>
  );
};

export default ProductPage;